import { useState } from 'react';
import { saveMyCourseAttendance, saveMyCourseGrade } from '@/lib/api';
import { buildClasswork, flatClasswork, kindLabel } from '@/lib/courseRoom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

/**
 * The teaching-only surfaces: who is in the class, and what they have earned.
 *
 * Both render inside the course room rather than on their own pages, so an
 * instructor marks the class from the same screen they teach it in.
 */
export function RosterTab({ students }) {
  if (!students.length) {
    return <p className="py-8 text-center text-sm text-[#062B1A]/45">No students are enrolled yet.</p>;
  }
  return (
    <div className="space-y-2" data-testid="room-roster">
      {students.map((student) => (
        <div key={student.id} className="grid gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-white p-3 sm:grid-cols-[1fr_160px_110px] sm:items-center">
          <div>
            <p className="text-sm font-semibold text-[#062B1A]">{student.name}</p>
            <p className="text-xs text-[#062B1A]/45">{student.student_number || student.email}</p>
          </div>
          <div>
            <Progress value={student.course_progress || 0} className="h-2" />
            <p className="mt-1 text-[10px] text-[#062B1A]/45">{student.course_progress || 0}% complete</p>
          </div>
          <Badge variant="outline" className="justify-center">{student.enrollment_status || 'Enrolled'}</Badge>
        </div>
      ))}
    </div>
  );
}

/**
 * Every marked item in this course, in stream order.
 *
 * The gradebook used to be a blank final-grade form: the instructor had to open
 * each piece of classwork, note its mark, and total them by hand. The marks
 * already exist on the submissions the room loads, so they are gathered here
 * instead.
 */
function assessmentsOf(assignments, exams, modules) {
  // Built through the same stream the classwork pane renders, so the gradebook
  // lists work in the order the class meets it — module by module — rather than
  // by each collection's own numbering.
  return flatClasswork(buildClasswork({ lessons: [], assignments, exams, modules }))
    .filter((item) => item.kind !== 'material');
}

/** One student's marks across those assessments, and what they add up to. */
function marksFor(assessments, submissions, studentId) {
  const rows = assessments.map((item) => {
    const submission = (submissions || []).find(
      (row) => row.reference_id === item.id && row.student_id === studentId,
    );
    const graded = submission?.status === 'graded';
    return {
      id: item.id,
      title: item.title,
      kind: kindLabel(item.kind),
      possible: Number(item.total_marks) || 0,
      score: graded ? Number(submission.score) || 0 : null,
      state: !submission ? 'not-handed-in' : (graded ? 'graded' : 'awaiting'),
    };
  });
  // Only marked work can be totalled — an unmarked hand-in would otherwise
  // count as zero and quietly understate the student.
  const graded = rows.filter((row) => row.state === 'graded');
  const earned = graded.reduce((sum, row) => sum + row.score, 0);
  const possible = graded.reduce((sum, row) => sum + row.possible, 0);
  return {
    rows,
    earned,
    possible,
    percentage: possible ? Math.round((earned / possible) * 100) : null,
    outstanding: rows.length - graded.length,
  };
}

export function GradebookTab({
  courseId, students, grades, attendance, assignments, exams, submissions, modules, onChanged,
}) {
  const [gradeDrafts, setGradeDrafts] = useState(() =>
    Object.fromEntries((grades || []).map((row) => [row.student_id, row])));
  const [attendanceDrafts, setAttendanceDrafts] = useState({});
  const [busyId, setBusyId] = useState('');
  const assessments = assessmentsOf(assignments, exams, modules);

  const saveGrade = async (student) => {
    const draft = gradeDrafts[student.id] || {};
    // An untouched percentage field shows the computed total; save that rather
    // than 0, so what the instructor saw is what gets written.
    const computed = marksFor(assessments, submissions, student.id).percentage;
    const percentage = draft.percentage ?? computed ?? 0;
    try {
      setBusyId(student.id);
      const { data } = await saveMyCourseGrade(courseId, {
        student_id: student.id,
        percentage: Number(percentage) || 0,
        letter_grade: draft.letter_grade || '',
        status: draft.status || 'draft',
        feedback: draft.feedback || '',
      });
      setGradeDrafts((prev) => ({ ...prev, [student.id]: data }));
      toast.success(`Grade saved for ${student.name}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save grade');
    } finally {
      setBusyId('');
    }
  };

  const saveAttendance = async (student) => {
    const draft = attendanceDrafts[student.id] || {};
    try {
      setBusyId(student.id);
      await saveMyCourseAttendance(courseId, {
        student_id: student.id,
        session_id: draft.session_id || '',
        attended_on: draft.attended_on || new Date().toISOString().slice(0, 10),
        status: draft.status || 'present',
        notes: draft.notes || '',
      });
      toast.success(`Attendance recorded for ${student.name}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save attendance');
    } finally {
      setBusyId('');
    }
  };

  if (!students.length) {
    return <p className="py-8 text-center text-sm text-[#062B1A]/45">No students are enrolled yet.</p>;
  }

  return (
    <div className="space-y-6" data-testid="room-gradebook">
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/40">Grades</p>
        {students.map((student) => {
          const draft = gradeDrafts[student.id] || {};
          const marks = marksFor(assessments, submissions, student.id);
          return (
            <div key={student.id} className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-white p-3">
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-[#062B1A]">{student.name}</p>
                  <p className="text-xs text-[#062B1A]/45">{student.student_number}</p>
                </div>
                <p className="text-sm font-semibold text-[#062B1A]" data-testid={`gradebook-total-${student.id}`}>
                  {marks.possible
                    ? <>{marks.earned}/{marks.possible} marked <span className="text-[#D4AF37]">· {marks.percentage}%</span></>
                    : <span className="font-normal text-[#062B1A]/45">Nothing marked yet</span>}
                </p>
              </div>

              {/* What the student has actually earned, item by item, so the
                  final grade is entered against the record rather than from
                  memory. */}
              {marks.rows.length > 0 && (
                <div className="mb-3 overflow-x-auto rounded-md border border-[#062B1A]/8">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#F7F8FA] text-left text-[10px] uppercase tracking-[0.08em] text-[#062B1A]/45">
                        <th scope="col" className="px-3 py-1.5 font-semibold">Assessment</th>
                        <th scope="col" className="px-3 py-1.5 font-semibold">Type</th>
                        <th scope="col" className="px-3 py-1.5 text-right font-semibold">Mark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marks.rows.map((row) => (
                        <tr key={row.id} className="border-t border-[#062B1A]/5">
                          <td className="px-3 py-1.5 text-[#062B1A]/80">{row.title}</td>
                          <td className="px-3 py-1.5 text-[#062B1A]/45">{row.kind}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">
                            {row.state === 'graded' && (
                              <span className="font-medium text-[#062B1A]">{row.score}/{row.possible}</span>
                            )}
                            {row.state === 'awaiting' && <span className="text-amber-600">Awaiting mark</span>}
                            {row.state === 'not-handed-in' && <span className="text-[#062B1A]/35">Not handed in</span>}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-[#062B1A]/10 bg-[#F7F8FA]">
                        <td className="px-3 py-1.5 font-semibold text-[#062B1A]" colSpan={2}>
                          Total of marked work
                          {marks.outstanding > 0 && (
                            <span className="ml-2 font-normal text-[10px] text-amber-700">
                              {marks.outstanding} outstanding
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-[#062B1A]">
                          {marks.possible ? `${marks.earned}/${marks.possible}` : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid gap-2 lg:grid-cols-[100px_90px_130px_1fr_auto] lg:items-start">
                <div>
                  <Input
                    id={`grade-pct-${student.id}`}
                    type="number"
                    min="0"
                    max="100"
                    aria-label={`${student.name} percentage`}
                    value={draft.percentage ?? (marks.percentage ?? '')}
                    placeholder="%"
                    onChange={(e) => setGradeDrafts((p) => ({ ...p, [student.id]: { ...draft, percentage: e.target.value } }))}
                  />
                  {marks.percentage !== null && (
                    <button
                      type="button"
                      className="mt-1 text-[10px] text-[#062B1A]/45 underline hover:text-[#062B1A]"
                      onClick={() => setGradeDrafts((p) => ({ ...p, [student.id]: { ...draft, percentage: marks.percentage } }))}
                    >
                      Use {marks.percentage}%
                    </button>
                  )}
                </div>
                <Input aria-label={`${student.name} letter grade`} value={draft.letter_grade || ''} placeholder="Grade" onChange={(e) => setGradeDrafts((p) => ({ ...p, [student.id]: { ...draft, letter_grade: e.target.value.toUpperCase() } }))} />
                <Select value={draft.status || 'draft'} onValueChange={(v) => setGradeDrafts((p) => ({ ...p, [student.id]: { ...draft, status: v } }))}>
                  <SelectTrigger aria-label={`${student.name} grade status`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea
                  aria-label={`${student.name} feedback`}
                  className="min-h-[64px]"
                  value={draft.feedback || ''}
                  placeholder="Feedback"
                  onChange={(e) => setGradeDrafts((p) => ({ ...p, [student.id]: { ...draft, feedback: e.target.value } }))}
                />
                <Button className="bg-[#062B1A] text-white" disabled={busyId === student.id} onClick={() => saveGrade(student)}>Save</Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/40">Attendance</p>
        {students.map((student) => {
          const draft = attendanceDrafts[student.id] || {};
          const present = (attendance || []).filter((row) => row.student_id === student.id && row.status === 'present').length;
          return (
            <div key={student.id} className="grid gap-2 rounded-lg border border-[rgba(6,43,26,0.08)] bg-white p-3 lg:grid-cols-[1fr_150px_140px_1fr_auto] lg:items-center">
              <div>
                <p className="text-sm font-semibold text-[#062B1A]">{student.name}</p>
                <p className="text-xs text-[#062B1A]/45">{present} recorded present</p>
              </div>
              <Input type="date" aria-label={`${student.name} date`} value={draft.attended_on || new Date().toISOString().slice(0, 10)} onChange={(e) => setAttendanceDrafts((p) => ({ ...p, [student.id]: { ...draft, attended_on: e.target.value } }))} />
              <Select value={draft.status || 'present'} onValueChange={(v) => setAttendanceDrafts((p) => ({ ...p, [student.id]: { ...draft, status: v } }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['present', 'absent', 'late', 'excused'].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input value={draft.notes || ''} placeholder="Optional note" onChange={(e) => setAttendanceDrafts((p) => ({ ...p, [student.id]: { ...draft, notes: e.target.value } }))} />
              <Button className="bg-[#062B1A] text-white" disabled={busyId === student.id} onClick={() => saveAttendance(student)}>Record</Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
